<script setup lang="ts">
/**
 * Публичная оферта.
 *
 * Числа и реквизиты берутся с сервера, а не набраны в тексте: документ
 * называет минимальную сумму, длину грейса и цену правки, и разойтись с
 * биллингом ему нельзя — в споре правым окажется он, а не код.
 */
import { formatRubles } from '@zetter/shared';
import { computed } from 'vue';
import LegalDocument from '../../components/legal/LegalDocument.vue';
import { legal } from '../../site/public-data.js';

const company = computed(() => legal.value?.company ?? null);
const terms = computed(() => legal.value?.terms ?? null);

/** Цена правки — витрина над копейками, и в документе она обязана быть в рублях. */
const creditPrice = computed(() =>
  terms.value ? formatRubles(terms.value.creditKopecks) : '—',
);
const minPayment = computed(() =>
  terms.value ? formatRubles(terms.value.minPaymentKopecks) : '—',
);
const graceDays = computed(() => terms.value?.graceDays ?? 0);
const vatLine = computed(() =>
  terms.value?.vatMode === 'vat20'
    ? 'Стоимость услуг включает НДС 20 %.'
    : 'Услуги НДС не облагаются: Исполнитель применяет упрощённую систему налогообложения.',
);
</script>

<template>
  <LegalDocument
    title="Публичная оферта"
    revision="21 августа 2026 г."
    subtitle="Договор возмездного оказания услуг сервиса Keypi. Настоящий документ является
      официальным предложением заключить договор на изложенных ниже условиях."
  >
    <h2 id="terms">1. Термины</h2>
    <p>
      <strong>Исполнитель</strong> — {{ company?.fullName || 'лицо, указанное в разделе 13' }},
      реквизиты приведены в разделе 13.
    </p>
    <p>
      <strong>Сервис</strong> — программно-аппаратный комплекс Keypi, доступный по адресу
      {{ legal?.appUrl || 'keypi.ru' }}, предназначенный для подготовки и правки документов с
      использованием моделей искусственного интеллекта.
    </p>
    <p>
      <strong>Заказчик</strong> — юридическое лицо, индивидуальный предприниматель или физическое
      лицо, принявшее условия настоящей оферты.
    </p>
    <p>
      <strong>Организация</strong> — рабочее пространство Заказчика в Сервисе: участники, проекты,
      баланс и подписка. Все расчёты ведутся по Организации, а не по отдельному пользователю.
    </p>
    <p>
      <strong>Правка</strong> — одно обращение к Сервису с задачей по документу и результат этого
      обращения.
    </p>
    <p>
      <strong>Кредит</strong> — расчётная единица объёма услуг, равная одной обычной правке.
      Стоимость кредита на дату публикации — {{ creditPrice }}. Кредит не является ни валютой, ни
      электронным средством платежа: это способ показать остаток в понятных Заказчику единицах.
    </p>
    <p>
      <strong>Баланс</strong> — сумма денежных средств, внесённых Заказчиком в счёт предстоящего
      оказания услуг, за вычетом стоимости оказанных услуг.
    </p>
    <p>
      <strong>План</strong> — тарифный план: набор ограничений Организации и включённый в него
      объём услуг на период. Действующая линейка планов опубликована на странице
      {{ legal?.appUrl || 'keypi.ru' }}/#pricing.
    </p>
    <p>
      <strong>Подписка</strong> — период действия оплаченного Плана.
    </p>

    <h2 id="subject">2. Предмет договора</h2>
    <p>
      2.1. Исполнитель предоставляет Заказчику доступ к Сервису и оказывает услуги по подготовке и
      правке документов, а Заказчик оплачивает их в порядке раздела 5.
    </p>
    <p>
      2.2. Договор считается заключённым с момента акцепта оферты. Акцептом признаётся любое из
      следующих действий: регистрация в Сервисе, начало использования Сервиса, оплата услуг.
    </p>
    <p>
      2.3. Совершая акцепт, Заказчик подтверждает, что ознакомился с настоящей офертой и
      <router-link to="/payment-rules">Правилами приёма платежей</router-link>, которые являются
      неотъемлемой частью договора.
    </p>
    <p>
      2.4. Услуги оказываются дистанционно. Место оказания услуг — место нахождения Исполнителя.
    </p>

    <h2 id="access">3. Регистрация и доступ</h2>
    <p>
      3.1. Для работы Заказчик создаёт учётную запись и Организацию. Учётная запись защищена
      паролем, который Заказчик обязан хранить в тайне.
    </p>
    <p>
      3.2. Действия, совершённые с использованием учётной записи Заказчика, считаются совершёнными
      Заказчиком. Об утрате доступа или несанкционированном использовании Заказчик уведомляет
      Исполнителя незамедлительно.
    </p>
    <p>
      3.3. Заказчик вправе приглашать в Организацию других пользователей и отвечает за их действия
      как за свои собственные.
    </p>

    <h2 id="service">4. Порядок оказания услуг</h2>
    <p>
      4.1. Услуга оказывается в момент выполнения правки: Сервис принимает задачу, обращается к
      моделям искусственного интеллекта и возвращает результат.
    </p>
    <p>
      4.2. Стоимость правки определяется фактическим объёмом обработанных данных и списывается с
      Баланса по завершении правки. Расшифровка каждого списания доступна Заказчику в разделе
      «Списания» и является актом об оказанных услугах в электронной форме.
    </p>
    <p>
      4.3. <strong>Результат работы Сервиса является черновиком.</strong> Он создаётся моделью
      искусственного интеллекта и может содержать фактические, юридические и расчётные ошибки.
      Заказчик обязан проверить результат перед использованием. Исполнитель не гарантирует
      пригодность результата для конкретной цели Заказчика.
    </p>
    <p>
      4.4. Услуга считается оказанной надлежащим образом, если Заказчик не направил мотивированных
      возражений в течение трёх рабочих дней с момента списания. Возражения направляются на адрес,
      указанный в разделе 13.
    </p>
    <p>
      4.5. Срок хранения версий документов определяется Планом. По истечении срока Исполнитель
      вправе удалить собранные версии; последняя версия проекта не удаляется.
    </p>

    <h2 id="price">5. Стоимость и порядок расчётов</h2>
    <p>
      5.1. Услуги оплачиваются авансом путём пополнения Баланса либо оплатой Плана. Минимальная
      сумма пополнения — {{ minPayment }}. {{ vatLine }}
    </p>
    <p>
      5.2. Способы оплаты, момент исполнения обязательства по оплате и порядок возврата средств
      установлены <router-link to="/payment-rules">Правилами приёма платежей</router-link>.
    </p>
    <p>
      5.3. Оплата Плана и пополнение Баланса — разные платежи с разным назначением. Оплата Плана
      предоставляет ограничения и включённый объём услуг на период и на Баланс не зачисляется.
    </p>
    <p>
      5.4. Включённый в План объём услуг предоставляется на период Подписки. Неиспользованный
      включённый объём переносится на следующий период в пределах, установленных Сервисом, и сверх
      этого предела сгорает. Оплаченные Заказчиком средства не сгорают.
    </p>
    <p>
      5.5. Исполнитель вправе изменять стоимость услуг и состав Планов. Изменения не применяются к
      уже оплаченному периоду Подписки. Новая стоимость публикуется на сайте и действует со дня
      публикации.
    </p>
    <p>
      5.6. При недостатке средств на Балансе оказание услуг приостанавливается. Исполнитель вправе
      предоставить Организации технический овердрафт; средства, использованные сверх Баланса,
      подлежат оплате при ближайшем пополнении.
    </p>

    <h2 id="subscription">6. Подписка и автоматическое продление</h2>
    <p>
      6.1. Подписка приобретается на один месяц или на двенадцать месяцев. При оплате за двенадцать
      месяцев предоставляется скидка
      <template v-if="terms?.annualDiscountPct">{{ terms.annualDiscountPct }} %</template>.
    </p>
    <p>
      6.2. <strong>Подписка продлевается автоматически</strong>, если Заказчик привязал способ
      оплаты и не отключил автоплатёж. Списание производится в день окончания оплаченного периода в
      размере стоимости следующего периода по действующим ценам. Порядок привязки, уведомления и
      отказа установлены разделом «Подписка и автоплатёж»
      <router-link to="/payment-rules">Правил приёма платежей</router-link>.
    </p>
    <p>
      6.3. Если автоплатёж не подключён, Исполнитель выставляет счёт на продление за
      {{ terms?.invoiceLeadDays ?? 5 }} дней до окончания периода.
    </p>
    <p>
      6.4. Если к окончанию периода оплата не поступила, Подписка сохраняет действие ещё
      {{ graceDays }} дней. По истечении этого срока Организация переводится на бесплатный план:
      ограничения снижаются, но участники, проекты и материалы Заказчика сохраняются. Добавление
      сверх нового лимита блокируется.
    </p>
    <p>
      6.5. Заказчик вправе отказаться от Подписки в любой момент. Отказ прекращает автоматическое
      продление; оплаченный период при этом действует до конца.
    </p>
    <p>
      6.6. Переход на более дорогой План внутри оплаченного периода оплачивается разницей за
      оставшиеся дни периода. Переход на более дешёвый План производится без доплаты; уплаченные
      средства за текущий период не возвращаются.
    </p>

    <h2 id="refund">7. Возврат средств</h2>
    <p>
      7.1. Заказчик вправе в любой момент отказаться от договора и потребовать возврата
      неиспользованного остатка Баланса.
    </p>
    <p>
      7.2. Возврату подлежат средства, фактически внесённые Заказчиком и не израсходованные на
      оказанные услуги. Бонусы, приветственные и включённые в План кредиты денежными средствами не
      являются и возврату не подлежат.
    </p>
    <p>
      7.3. Стоимость Плана за начатый период возврату не подлежит: ограничения и включённый объём
      предоставлены на весь период.
    </p>
    <p>
      7.4. Заявление на возврат направляется на адрес, указанный в разделе 13, с указанием
      реквизитов Организации. Срок рассмотрения — до десяти рабочих дней; срок возврата и способ
      установлены разделом «Возврат средств»
      <router-link to="/payment-rules">Правил приёма платежей</router-link>.
    </p>

    <h2 id="duties">8. Права и обязанности сторон</h2>
    <h3>Исполнитель обязуется</h3>
    <ul>
      <li>обеспечивать доступность Сервиса и оказывать услуги в соответствии с настоящей офертой;</li>
      <li>обеспечивать сохранность материалов Заказчика в течение срока, установленного Планом;</li>
      <li>не передавать материалы Заказчика третьим лицам, кроме случаев раздела 11.</li>
    </ul>
    <h3>Исполнитель вправе</h3>
    <ul>
      <li>привлекать третьих лиц для оказания услуг, оставаясь ответственным за результат;</li>
      <li>
        приостановить доступ при нарушении Заказчиком настоящей оферты, при отрицательном Балансе
        или при использовании Сервиса способом, создающим угрозу его работоспособности;
      </li>
      <li>
        проводить плановые технические работы, уведомляя о них заранее, если работы приводят к
        перерыву в доступе.
      </li>
    </ul>
    <h3>Заказчик обязуется</h3>
    <ul>
      <li>оплачивать услуги в порядке раздела 5;</li>
      <li>
        не загружать в Сервис материалы, распространение которых запрещено законом, а также
        материалы, права на которые ему не принадлежат;
      </li>
      <li>
        не использовать Сервис для создания материалов, нарушающих закон, и проверять результат
        перед использованием (п. 4.3);
      </li>
      <li>
        обеспечить наличие правовых оснований для передачи Исполнителю персональных данных третьих
        лиц, если такие данные содержатся в материалах Заказчика.
      </li>
    </ul>

    <h2 id="liability">9. Ответственность и ограничения</h2>
    <p>
      9.1. Сервис предоставляется на условиях «как есть». Исполнитель не гарантирует бесперебойной
      работы: доступность зависит в том числе от сторонних поставщиков моделей искусственного
      интеллекта и каналов связи.
    </p>
    <p>
      9.2. Исполнитель не несёт ответственности за последствия использования результата работы
      Сервиса без проверки, за упущенную выгоду, а также за решения, принятые Заказчиком на
      основании подготовленных документов.
    </p>
    <p>
      9.3. Совокупная ответственность Исполнителя по договору ограничена суммой, фактически
      уплаченной Заказчиком за три месяца, предшествующих событию, повлёкшему ответственность.
    </p>
    <p>
      9.4. Стороны освобождаются от ответственности при обстоятельствах непреодолимой силы, включая
      прекращение доступа к сторонним сервисам по не зависящим от Исполнителя причинам.
    </p>

    <h2 id="rights">10. Права на результаты</h2>
    <p>
      10.1. Материалы, загруженные Заказчиком, и документы, подготовленные по его заданию,
      принадлежат Заказчику. Исполнитель не приобретает на них прав, кроме права обрабатывать их для
      оказания услуг.
    </p>
    <p>
      10.2. Права на Сервис, его программное обеспечение и шаблоны, предоставленные Исполнителем,
      принадлежат Исполнителю. Заказчик получает право использовать их в объёме, необходимом для
      работы в Сервисе, на срок действия договора.
    </p>
    <p>
      10.3. Шаблоны, созданные Заказчиком, остаются его собственностью. Публикация такого шаблона
      для других организаций производится только по решению Заказчика.
    </p>

    <h2 id="data">11. Персональные данные и конфиденциальность</h2>
    <p>
      11.1. Исполнитель обрабатывает персональные данные Заказчика и его пользователей в объёме,
      необходимом для исполнения договора: имя, адрес электронной почты, сведения об организации и
      о платежах.
    </p>
    <p>
      11.2. Материалы Заказчика передаются поставщикам моделей искусственного интеллекта
      исключительно для выполнения его задачи. Для обучения моделей материалы Заказчика
      Исполнителем не передаются.
    </p>
    <p>
      11.3. Платёжные данные Заказчика Исполнителю не передаются и им не хранятся: их принимает
      банк-эквайер (раздел «Безопасность платежей»
      <router-link to="/payment-rules">Правил приёма платежей</router-link>).
    </p>
    <p>
      11.4. Стороны обязуются не раскрывать содержание материалов и условий работы третьим лицам,
      кроме случаев, предусмотренных законом.
    </p>
    <p>
      11.5. Состав обрабатываемых персональных данных, цели и правовые основания обработки, перечень
      привлечённых обработчиков, сроки хранения и порядок реализации прав субъектов описаны в
      <router-link to="/privacy">Политике обработки персональных данных</router-link>.
    </p>

    <h2 id="validity">12. Срок действия и изменение оферты</h2>
    <p>
      12.1. Договор действует бессрочно до отказа одной из сторон.
    </p>
    <p>
      12.2. Исполнитель вправе изменять оферту. Новая редакция публикуется на этой странице и
      вступает в силу со дня публикации. Продолжение использования Сервиса означает согласие с новой
      редакцией; при несогласии Заказчик вправе отказаться от договора в порядке раздела 7.
    </p>
    <p>
      12.3. Споры разрешаются путём переговоров. Претензионный порядок обязателен, срок ответа на
      претензию — тридцать календарных дней. При недостижении согласия спор рассматривается судом по
      месту нахождения Исполнителя в соответствии с законодательством Российской Федерации.
    </p>

    <h2 id="requisites">13. Реквизиты Исполнителя</h2>
    <dl v-if="company">
      <dt>Наименование</dt>
      <dd>{{ company.fullName || company.name || '—' }}</dd>
      <dt>ИНН</dt>
      <dd>{{ company.inn || '—' }}</dd>
      <template v-if="company.kpp">
        <dt>КПП</dt>
        <dd>{{ company.kpp }}</dd>
      </template>
      <dt>ОГРН/ОГРНИП</dt>
      <dd>{{ company.ogrn || '—' }}</dd>
      <dt>Адрес</dt>
      <dd>{{ company.address || '—' }}</dd>
      <template v-if="company.bankAccount">
        <dt>Расчётный счёт</dt>
        <dd>{{ company.bankAccount }}</dd>
        <dt>Банк</dt>
        <dd>{{ company.bankName }}, БИК {{ company.bankBik }}</dd>
        <dt>Корр. счёт</dt>
        <dd>{{ company.bankCorrAccount }}</dd>
      </template>
      <dt>Электронная почта</dt>
      <dd>{{ company.email || '—' }}</dd>
      <template v-if="company.phone">
        <dt>Телефон</dt>
        <dd>{{ company.phone }}</dd>
      </template>
    </dl>
    <p v-else class="text-gray-500">Реквизиты загружаются…</p>
  </LegalDocument>
</template>
